import { useState } from 'react';
import { cn } from '@/lib/utils';
import { usePostComment } from '@/lib/comments/api';
import { commentErrorMessage } from './comment-errors';

export const COMMENT_MAX_LENGTH = 1000;

export interface CommentComposerProps {
  eventId: string;
  /** Present when composing a reply — threads under this (top-level) comment. */
  parentId?: string;
  placeholder?: string;
  autoFocus?: boolean;
  /** Fired after a successful post (e.g. to collapse an inline reply box). */
  onPosted?: () => void;
}

/**
 * Comment / reply composer (Figma 1727-78418 "Comments"): a single bordered
 * field with the Post button embedded on the right. Feedback is inline (no
 * toast system): the post error renders under the field and turns its border
 * red. A 403 KYC_REQUIRED never surfaces here — the global interceptor opens
 * the KYC gate and replays the post on approval.
 */
export function CommentComposer({ eventId, parentId, placeholder, autoFocus, onPosted }: CommentComposerProps) {
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { mutate: post, isPending } = usePostComment({
    mutation: {
      onSuccess: () => {
        setBody('');
        setError(null);
        onPosted?.();
      },
      onError: (err) => setError(commentErrorMessage(err)),
    },
  });

  const trimmed = body.trim();
  const submit = () => {
    if (!trimmed || isPending) return;
    setError(null);
    post({ eventId, data: { body: trimmed, ...(parentId ? { parentId } : {}) } });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={cn(
          'flex items-center gap-2 rounded-[12px] border-2 bg-surface py-2 pl-4 pr-2 transition-colors',
          error ? 'border-danger' : 'border-stroke-soft focus-within:border-accent',
        )}
      >
        <textarea
          rows={1}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={placeholder ?? 'Add a comment'}
          maxLength={COMMENT_MAX_LENGTH}
          autoFocus={autoFocus}
          disabled={isPending}
          className={cn(
            'field-sizing-content max-h-28 min-h-6 flex-1 resize-none border-none bg-transparent',
            'font-sans text-base text-primary placeholder:text-muted focus:outline-none',
            'disabled:opacity-50',
          )}
          onKeyDown={(e) => {
            // Enter posts; Shift+Enter makes a newline (chat convention). Skip
            // the confirm-keystroke of an in-progress IME composition (CJK input).
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!trimmed || isPending}
          className={cn(
            'shrink-0 cursor-pointer rounded-[10px] bg-purple px-4 py-1',
            'font-sans text-base font-medium capitalize text-white',
            'transition-opacity hover:opacity-90 active:opacity-80',
            'disabled:pointer-events-none disabled:opacity-70',
          )}
        >
          {isPending ? 'Posting…' : parentId ? 'Reply' : 'Post'}
        </button>
      </div>
      {error && <p className="text-sm font-light text-danger">{error}</p>}
    </div>
  );
}
